import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Col, Form, Row,
} from 'react-bootstrap';
import { store } from 'redux/store';
import { selectMailerLayoutIsDanger, setIsDanger } from 'redux/reducers/mailerLayoutReducer';
import CloseButton from 'components/CloseButton';
import useReportForm from 'utils/hooks/mailer/useReportForm';
import { onTextChange } from 'helper/controlledField';
import AlertsCollection from 'components/AlertsCollection';
import Checkbox from 'components/DesignSystem/Checkbox';
import Button from 'components/DesignSystem/Button';
import { useClosure } from 'utils/hooks';
import DatePicker from 'components/DatePicker';
import RichTextEditor from 'components/RichTextEditor';
import ReportPreview from './ReportPreview';
import ProductSelect from './ProductSelect';
import SubtypeSelect from './SubtypeSelect';
import CompanySelect from './CompanySelect';
import ReportSubmission from './ReportSubmission';

import './index.scss';

export default function ReportForm({
  close,
}: {
  close: () => void,
}) {
  const isDanger = useSelector(selectMailerLayoutIsDanger);
  const {
    isTest,
    setIsTest,
    types,
    typeId,
    setTypeId,
    validType,
    subtypes,
    subtypeId,
    setSubtypeId,
    validSubtype,
    date,
    setDate,
    validDate,
    file,
    updateFile,
    fileRef,
    validFile,
    invalidFile,
    invalidFileMessage,
    preview,
    setPreview,
    companyIds,
    setCompanyIds,
    clearInputsDisabled,
    clearInputs,
    submissionDisabled,
    showCompanySelect,
    submitReport,
    submissionData,
    alerts,
    cancellationStatus,
    cancelReport,
    companies,
    companiesLoading,
    title,
    setTitle,
    content,
    setContent,
  } = useReportForm();

  useEffect(() => {
    store.dispatch(setIsDanger(!isTest));

    return () => {
      store.dispatch(setIsDanger(false));
    };
  }, [isTest]);

  return (
    <div>
      <div className={`report-form-header px-3 py-3 ${isDanger ? 'danger' : ''}`}>
        <div className="d-flex justify-content-between align-items-end">
          <CloseButton
            id="close-new-report"
            label="Novo Relatório"
            onClick={close}
          />
          <div className="mx-4 d-flex">
            <Checkbox className="me-2" checked={isTest} onChange={() => setIsTest(!isTest)} />
            Envio de testes
          </div>
        </div>
      </div>
      <hr className="mt-0 mb-2" />
      <div className="p-3">
        <AlertsCollection alerts={alerts} />
        <Row className="align-items-end mt-4 mb-2">
          <Col xs={12} md className="mb-3 mb-md-0">
            <ProductSelect
              types={types}
              typeId={typeId}
              setTypeId={setTypeId}
              setSubtypeId={setSubtypeId}
              isValid={validType}
            />
          </Col>
          <Col xs={12} md className="mb-3 mb-md-0">
            <SubtypeSelect
              subtypes={subtypes}
              subtypeId={subtypeId}
              setSubtypeId={setSubtypeId}
              isValid={validSubtype}
            />
          </Col>
          <Col xs={12} md className="mb-3 mb-md-0">
            <DatePicker
              isClearable
              placeholderText="Data"
              selected={date}
              onChange={(d) => setDate(d)}
              className={`report-form-date-picker ${validDate ? 'report-form-valid-border' : ''}`}
            />
          </Col>
          <Col xs={12} md className="text-center">
            <Button
              icon="visible"
              id="report-preview-button"
              onClick={() => setPreview(true)}
            >
              Visualizar
            </Button>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col xs={12} md={3}>
            <Form.Control
              type="file"
              className="rounded-0"
              ref={fileRef}
              onChange={updateFile}
              isValid={validFile}
              isInvalid={invalidFile}
            />
            <Form.Control.Feedback type="invalid">
              {invalidFileMessage}
            </Form.Control.Feedback>
          </Col>
        </Row>
        <Row className="mb-3">
          <Form.Group>
            <Form.Label>Título</Form.Label>
            <Form.Control
              value={title}
              onChange={onTextChange(setTitle)}
            />
          </Form.Group>
        </Row>
        <Row className="mb-4">
          <Form.Group>
            <Form.Label>Conteúdo</Form.Label>
            <RichTextEditor
              value={content}
              onChange={useClosure(setContent)}
            />
          </Form.Group>
        </Row>
        <div className="d-flex pb-3 mb-4">
          <Button
            className="me-2"
            type="secondary"
            disabled={clearInputsDisabled}
            onClick={clearInputs}
          >
            Limpar
          </Button>
          <Button
            disabled={submissionDisabled}
            onClick={submitReport}
          >
            Enviar
          </Button>
        </div>
        {showCompanySelect && (
          <CompanySelect
            companies={companies}
            selectedCompanyIds={companyIds}
            companiesAreLoading={companiesLoading}
            setSelectedCompanyIds={setCompanyIds}
          />
        )}
        {submissionData && cancellationStatus !== 'success' && (
          <ReportSubmission
            filename={file?.name}
            submissionData={submissionData}
            cancelReport={cancelReport}
          />
        )}
        <ReportPreview
          show={preview}
          handleClose={() => setPreview(false)}
          type={typeId}
          subType={subtypeId}
          printDate={date?.toLocaleDateString('PT-BR') || ''}
        />
      </div>
    </div>
  );
}
